/** Labels and the map of the admin area (English source strings, marked with msg()). */
import type { LucideIcon } from 'lucide-react';
import {
  Bot,
  Building2,
  FolderKanban,
  HeartPulse,
  KeyRound,
  Landmark,
  Megaphone,
  ScrollText,
  ShieldCheck,
  SlidersHorizontal,
  UserCog,
  Users,
} from 'lucide-react';
import type { PermissionCheck } from '@/lib/can';
import { msg, t } from '@/lib/i18n';
import type { Tone } from '@/ui';

export interface AdminArea {
  key: string;
  label: string;
  description: string;
  to: string;
  icon: LucideIcon;
  permission: PermissionCheck;
  /** Toecan staff only (platform keys). */
  platform?: boolean;
}

/** Every admin destination, in the order of the hub and the sidebar. */
export const AREAS: AdminArea[] = [
  {
    key: 'company',
    label: msg('Company'),
    description: msg('Name, time zone, language and branding.'),
    to: '/admin/company',
    icon: Building2,
    permission: ['core.company.read', 'core.company.manage'],
  },
  {
    key: 'projects',
    label: msg('Projects'),
    description: msg('The operational scopes: their modules, settings and people.'),
    to: '/admin/projects',
    icon: FolderKanban,
    permission: 'core.project.read',
  },
  {
    key: 'users',
    label: msg('People'),
    description: msg('Invite, edit, deactivate; see what each person may do.'),
    to: '/admin/users',
    icon: Users,
    permission: 'core.user.read',
  },
  {
    key: 'roles',
    label: msg('Roles and personas'),
    description: msg('Named sets of permissions, and how they differ from the templates.'),
    to: '/admin/roles',
    icon: UserCog,
    permission: 'core.user.read',
  },
  {
    key: 'grants',
    label: msg('Access grants'),
    description: msg('Who holds which role where — including people of other companies and grants that expire.'),
    to: '/admin/grants',
    icon: ShieldCheck,
    permission: 'core.user.read',
  },
  {
    key: 'api-keys',
    label: msg('API keys'),
    description: msg('Machine access: created, rotated and revoked here; secrets are shown once.'),
    to: '/admin/api-keys',
    icon: KeyRound,
    permission: 'core.apikey.manage',
  },
  {
    key: 'settings',
    label: msg('Settings'),
    description: msg('Every configurable value, where it comes from, and where it is overridden.'),
    to: '/admin/settings',
    icon: SlidersHorizontal,
    permission: 'core.setting.read',
  },
  {
    key: 'news',
    label: msg('News'),
    description: msg("Announcements on your people's home page: drafts, scheduled and published."),
    to: '/admin/news',
    icon: Megaphone,
    permission: 'admin.news.manage',
  },
  {
    key: 'access-log',
    label: msg('Access log'),
    description: msg('Who looked at personal data, and when.'),
    to: '/admin/access-log',
    icon: ScrollText,
    permission: 'admin.accesslog.read',
  },
  {
    key: 'ai',
    label: msg('AI usage and policy'),
    description: msg('Calls, cost and budget of the assistant, and what it may do without asking.'),
    to: '/admin/ai',
    icon: Bot,
    permission: 'core.ai.admin',
  },
  {
    key: 'system',
    label: msg('System health'),
    description: msg('Background jobs and schedules, and anything overdue.'),
    to: '/admin/system',
    icon: HeartPulse,
    permission: 'core.system.read',
  },
  {
    key: 'companies',
    label: msg('Companies'),
    description: msg('Toecan staff: provision companies and set their commercial modules.'),
    to: '/admin/companies',
    icon: Landmark,
    permission: 'platform.company.read',
    platform: true,
  },
];

export const ADMIN_PERMISSIONS = AREAS.flatMap((a) => (typeof a.permission === 'string' ? [a.permission] : [...a.permission]));

/**
 * Who gets Admin in the navigation: anyone with an admin area beyond reading the project list.
 * `core.project.read` alone (coach, dispatcher, compliance officer, installer, insurer) is a
 * scope, not an admin job — those people pick projects in the top bar, and /admin/projects stays
 * open to them by its route permission (deep links, search, the assistant's links).
 */
export const ADMIN_NAV_PERMISSIONS = ADMIN_PERMISSIONS.filter((p) => p !== 'core.project.read');

// ---------------------------------------------------------------- access

export const SCOPES = {
  company: { label: msg('Whole company'), help: msg('Everything in the company, now and later.') },
  projects: { label: msg('Projects'), help: msg('Only the chosen projects.') },
  groups: { label: msg('Groups'), help: msg('Only the members of the chosen groups (and their sub-groups).') },
  self: { label: msg('Own data only'), help: msg('Only what concerns the person themselves (a driver who is also a user).') },
} as const;

export function scopeLabel(scope: string): string {
  const d = SCOPES[scope as keyof typeof SCOPES];
  return d ? t(d.label) : scope;
}

export const INVITE: Record<string, { label: string; tone: Tone }> = {
  pending: { label: msg('Invited'), tone: 'info' },
  expired: { label: msg('Invitation expired'), tone: 'caution' },
  accepted: { label: msg('Signed in'), tone: 'good' },
  never_signed_in: { label: msg('Never signed in'), tone: 'neutral' },
};

// ---------------------------------------------------------------- settings provenance

export const SOURCES: Record<string, { label: string; tone: Tone }> = {
  vehicle: { label: msg('Vehicle'), tone: 'accent' },
  group: { label: msg('Group'), tone: 'info' },
  project: { label: msg('Project'), tone: 'good' },
  company: { label: msg('Company'), tone: 'medium' },
  device_profile: { label: msg('Device profile'), tone: 'caution' },
  default: { label: msg('Default'), tone: 'neutral' },
};

export function sourceLabel(source: string): string {
  const d = SOURCES[source];
  return d ? t(d.label) : source;
}

export const LEVEL_ORDER = ['vehicle', 'group', 'project', 'company'] as const;

// ---------------------------------------------------------------- misc

export const LOCALES = [
  { value: 'en', label: 'English' },
  { value: 'nl', label: 'Nederlands' },
  { value: 'fr', label: 'Français' },
  { value: 'de', label: 'Deutsch' },
];

export const RISK: Record<string, { label: string; tone: Tone }> = {
  low: { label: msg('Low risk'), tone: 'good' },
  medium: { label: msg('Medium risk'), tone: 'medium' },
  high: { label: msg('High risk'), tone: 'bad' },
};

/** Common IANA zones first; any other valid zone can still be typed. */
export const TIMEZONES = [
  'Europe/Brussels',
  'Europe/Amsterdam',
  'Europe/Paris',
  'Europe/Luxembourg',
  'Europe/Berlin',
  'Europe/London',
  'Europe/Madrid',
  'Europe/Rome',
  'Europe/Warsaw',
  'Europe/Lisbon',
  'Europe/Dublin',
  'UTC',
];

/** "coaching.plan" → "Coaching plan"; purposes and resources are keys, not prose. */
export function humanKey(key: string): string {
  const s = key.replace(/[._]+/g, ' ').trim();
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : '—';
}

/** What an AI call was for (the ledger's `purpose`), in words; unknown purposes fall back to the key. */
const AI_PURPOSES: Record<string, string> = {
  assistant: msg('Assistant'),
  'home.briefing': msg('Daily briefing on Home'),
  'reports.ai_summary': msg('Summaries in reports'),
  'quality.audit': msg('Data-quality audits'),
  'coaching.prep': msg('Coaching preparation'),
  'flows.draft': msg('Drafting automations'),
  'flows.ai_step': msg('AI steps in automations'),
  'comms.translate': msg('Translating messages'),
  'comms.interpret_reply': msg('Reading driver replies'),
};

export function aiPurposeLabel(key: string): string {
  const known = AI_PURPOSES[key];
  if (known) return t(known);
  if (key.startsWith('comms.draft.')) return t('Drafting messages');
  return humanKey(key);
}
